import { ROUTE_STYLES, isCleanAscent } from './ascentTypes.js';
import { convertToEwbanks, convertToVGrade } from './gradeUtils.js';
import { processAttempts } from './attemptUtils.js';

export const processLogbook = (records) => {
  const { processed, errors } = processLogbookWithErrors(records);
  return processed;
};

export const processLogbookWithErrors = (records) => {
  const processed = [];
  const errors = [];

  try {
    if (!Array.isArray(records) || records.length === 0) {
      throw new Error('No records found in the file');
    }

    // Basic filtering and error collection
    records.forEach(record => {
      if (!record || typeof record !== 'object') {
        errors.push({
          route: 'Unknown',
          date: 'Unknown',
          reason: 'Invalid record structure',
          rawData: record
        });
        return;
      }

      try {
        // Required fields validation
        if (!record["Route ID"]) {
          throw new Error('Missing Route ID');
        }
        if (!record["Route Name"]) {
          throw new Error('Missing Route Name');
        }
        if (!record["Ascent Date"]) {
          throw new Error('Missing Ascent Date');
        }
        if (!record["Ascent Type"]) {
          throw new Error('Missing Ascent Type');
        }

        // Date validation
        const ascentDate = new Date(record["Ascent Date"]);
        if (isNaN(ascentDate.getTime())) {
          throw new Error('Invalid ascent date format');
        }

        // Validate ascent style
        if (record["Ascent Gear Style"] === "Free solo") {
          throw new Error('Free solo ascents are not processed');
        }

        // Validate ascent type
        if (["Target", "Mark", "Tick", "Lead", "Lead solo", "Roped Solo", "Second"].includes(record["Ascent Type"])) {
          throw new Error(`Ascent type "${record["Ascent Type"]}" is not processed`);
        }

        // Validate route style
        if (["Ice", "Alpine"].includes(record["Route Gear Style"])) {
          throw new Error(`Route style "${record["Route Gear Style"]}" is not processed`);
        }

        // Determine climbing style
        const routeStyle = record["Route Gear Style"];
        let climbingStyle;
        if (routeStyle === "Boulder") {
          climbingStyle = ROUTE_STYLES.BOULDER;
        } else if (routeStyle === "Trad") {
          climbingStyle = ROUTE_STYLES.TRAD;
        } else if (routeStyle === "Mixed") {
          climbingStyle = ROUTE_STYLES.MIXED;
        } else if (routeStyle === "Aid") {
          climbingStyle = ROUTE_STYLES.AID;
        } else {
          climbingStyle = ROUTE_STYLES.SPORT;
        }

        // Process grades
        const grade = record["Ascent Grade"] || record["Route Grade"];
        if (!grade) {
          throw new Error('Missing route grade');
        }

        let vgrade = null;
        let ewbanks = null;
        
        try {
          if (climbingStyle === ROUTE_STYLES.BOULDER) {
            vgrade = convertToVGrade(grade);
            if (vgrade === null) {
              throw new Error('Invalid boulder grade format');
            }
          } else {
            ewbanks = convertToEwbanks(grade);
            if (ewbanks === null) {
              throw new Error('Invalid route grade format');
            }
          }
        } catch (gradeError) {
          throw new Error(`Grade conversion failed: ${gradeError.message}`);
        }

        const processedRecord = {
          ...record,
          year: ascentDate.getUTCFullYear(),
          month: ascentDate.getUTCMonth() + 1,
          day: ascentDate.getUTCDate(),
          height: record["Route Height"] ? 
            parseFloat(record["Route Height"].replace(/m$/, '')) : 0,
          vgrade,
          ewbanks,
          isClean: isCleanAscent(record["Ascent Type"], climbingStyle)
        };

        processed.push(processedRecord);
      } catch (error) {
        errors.push({
          route: record['Route Name'] || 'Unknown',
          date: record['Ascent Date'] || 'Unknown',
          reason: error.message,
          rawData: record
        });
      }
    });

    // Process attempts after collecting valid records
    const processedWithAttempts = processAttempts(processed);

    console.log('Processing complete:', {
      originalCount: records.length,
      validCount: processedWithAttempts.length,
      errorCount: errors.length,
      firstAttempts: processedWithAttempts.filter(r => r.isFirstAttempt).length
    });

    return { 
      processed: processedWithAttempts,
      errors 
    };
  } catch (error) {
    console.error('Processing failed:', error);
    throw new Error(`Error processing records: ${error.message}`);
  }
};